import moment from 'moment';
import { ShiftRequest } from 'types/shift-requests';
import { isSameDayIri } from './availabilityDisplay';
import { formatTimeOffPeriod } from './timeOffRequestForm';

export type ShiftLike = {
	id?: number;
	'@id'?: string;
	start?: Date | string;
	end?: Date | string;
	startAt?: string;
	endAt?: string;
	start_at?: string;
	end_at?: string;
	employee?: unknown;
};

export type DayLike = {
	'@id'?: string;
	id?: number;
	label?: string;
};

const isShiftLike = (value: unknown): value is ShiftLike =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const getShiftStart = (shift: ShiftLike | null | undefined): Date | string | undefined =>
	shift?.startAt || shift?.start_at || shift?.start;

const getShiftEnd = (shift: ShiftLike | null | undefined): Date | string | undefined =>
	shift?.endAt || shift?.end_at || shift?.end;

export const getShiftRefKeys = (shiftRef: unknown): string[] => {
	if (shiftRef == null || shiftRef === '') return [];

	const values: Array<string | number> = [];
	if (typeof shiftRef === 'string' || typeof shiftRef === 'number') {
		values.push(shiftRef);
	} else if (isShiftLike(shiftRef)) {
		if (shiftRef['@id']) values.push(shiftRef['@id']);
		if (shiftRef.id != null) values.push(shiftRef.id);
	}

	const keys = new Set<string>();
	values.forEach((value) => {
		const raw = String(value).trim();
		if (!raw) return;

		keys.add(raw);
		const noLeadingSlash = raw.replace(/^\//, '');
		keys.add(noLeadingSlash);
		keys.add(`/${noLeadingSlash}`);

		const idMatch = noLeadingSlash.match(/(?:(?:api\/)?shifts\/)?(\d+)$/i);
		if (idMatch) {
			const id = idMatch[1];
			keys.add(id);
			keys.add(`/api/shifts/${id}`);
			keys.add(`api/shifts/${id}`);
		}
	});

	return [...keys];
};

export const buildShiftsMap = (shifts: unknown[] = []): Map<string, ShiftLike> => {
	const map = new Map<string, ShiftLike>();
	shifts.forEach((shift) => {
		if (!isShiftLike(shift)) return;
		getShiftRefKeys(shift).forEach((key) => {
			map.set(key, shift);
		});
	});
	return map;
};

export const lookupShift = (
	shiftRef: unknown,
	shiftsMap?: Map<string, ShiftLike> | null
): ShiftLike | null => {
	if (shiftRef == null || shiftRef === '') return null;

	if (shiftsMap) {
		for (const key of getShiftRefKeys(shiftRef)) {
			const found = shiftsMap.get(key);
			if (found) return found;
		}
	}

	return isShiftLike(shiftRef) ? shiftRef : null;
};

export const formatShiftDateRange = (shift: ShiftLike | null | undefined): string | null => {
	const start = getShiftStart(shift);
	const end = getShiftEnd(shift);
	if (!start || !end) return null;

	return `${moment(start).format('MM/DD/YYYY h:mm A')} - ${moment(end).format('h:mm A')}`;
};

export const extractNumericShiftId = (shiftRef: unknown): number | null => {
	if (shiftRef == null || shiftRef === '') return null;
	if (isShiftLike(shiftRef) && typeof shiftRef.id === 'number') {
		return shiftRef.id;
	}

	for (const key of getShiftRefKeys(shiftRef)) {
		if (/^\d+$/.test(key)) return Number(key);
	}

	return null;
};

export const normalizeFetchedShift = (shift: unknown): ShiftLike | null => {
	if (!isShiftLike(shift)) return null;

	const startAt = getShiftStart(shift);
	const endAt = getShiftEnd(shift);
	const id = shift.id;

	return {
		...shift,
		id,
		'@id': shift['@id'] || (id != null ? `/api/shifts/${id}` : undefined),
		startAt: typeof startAt === 'string' ? startAt : shift.startAt,
		endAt: typeof endAt === 'string' ? endAt : shift.endAt,
		start: startAt ? moment(startAt).toDate() : shift.start,
		end: endAt ? moment(endAt).toDate() : shift.end,
	};
};

export const collectUnresolvedShiftIds = (
	requests: ShiftRequest[] = [],
	shiftsMap?: Map<string, ShiftLike> | null
): number[] => {
	const ids = new Set<number>();

	requests.forEach((request) => {
		[request.shiftRequest, request.shiftAssigned].forEach((ref) => {
			if (!ref) return;
			if (formatShiftDateRange(lookupShift(ref, shiftsMap))) return;

			const id = extractNumericShiftId(ref);
			if (id != null) ids.add(id);
		});
	});

	return [...ids].sort((a, b) => a - b);
};

const getDayLabel = (dayRef: string | null | undefined, allDays?: DayLike[] | null): string => {
	const day = allDays?.find(
		(d) => isSameDayIri(d['@id'], dayRef) || isSameDayIri(`/api/days/${d.id}`, dayRef)
	);
	return day?.label || 'Unknown Day';
};

export const getShiftDates = (
	request: ShiftRequest,
	options?: {
		shiftsMap?: Map<string, ShiftLike> | null;
		allDays?: DayLike[] | null;
	}
): string => {
	const shiftsMap = options?.shiftsMap;
	const allDays = options?.allDays;

	if (request.day && request.startAt && request.endAt) {
		const dayName = getDayLabel(request.day, allDays);
		const startTime = moment(request.startAt).format('h:mm A');
		const endTime = moment(request.endAt).format('h:mm A');
		return `${dayName}: ${startTime} - ${endTime}`;
	}

	if (request.day && request.dayStartTime && request.dayEndTime) {
		const dayName = getDayLabel(request.day, allDays);
		const startTime = moment(request.dayStartTime, 'HH:mm:ss').format('h:mm A');
		const endTime = moment(request.dayEndTime, 'HH:mm:ss').format('h:mm A');
		return `${dayName}: ${startTime} - ${endTime}`;
	}

	const fromShiftRequest = formatShiftDateRange(lookupShift(request.shiftRequest, shiftsMap));
	if (fromShiftRequest) return fromShiftRequest;

	const fromShiftAssigned = formatShiftDateRange(lookupShift(request.shiftAssigned, shiftsMap));
	if (fromShiftAssigned) return fromShiftAssigned;

	if (request.startAt && request.endAt) {
		const requestType = request?.type?.label?.toLowerCase() || '';
		if (requestType.includes('time off') || requestType.includes('time-off')) {
			return formatTimeOffPeriod(request.startAt, request.endAt);
		}
		return `${moment(request.startAt).format('MM/DD/YYYY h:mm A')} - ${moment(request.endAt).format('h:mm A')}`;
	}

	return 'Not specified';
};
